import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Box, Button, SxProps, Theme, Typography } from '@mui/material';
import React from 'react';

import { DEFAULT_ERROR_MESSAGE } from '../../../constants/errors';
import {
  CLEAR_FILTERS_LABEL,
  CLEAR_SEARCH_LABEL,
  FULL_BUDGET_BODY,
  FULL_BUDGET_BODY_MAX_WIDTH,
  FULL_BUDGET_TITLE,
  getNoResultsTitle,
  getNothingElseFitsTitle,
  NOTHING_ELSE_FITS_BODY,
  NOTHING_FITS_TEXT,
  NO_FILTER_MATCH_BODY,
  NO_FILTER_MATCH_TITLE,
  NO_FILTER_MATCH_WITH_SEARCH_BODY,
  NO_RESULTS_BODY,
  SHOW_ALL_ITEMS_LABEL,
} from '../../../constants/marketplace';
import { IError } from '../../../types/store';

interface IProps {
  error: IError | null;
  hasSpentCredits: boolean;
  // Blank when several values apply.
  filterName: string;
  hasFilters: boolean;
  query: string;
  hasPicksToReturnTo: boolean;
  onRetry: () => void;
  onOpenCart: () => void;
  onShowAllItems: () => void;
  onClearFilters: () => void;
  onClearFiltersKeepBudget: () => void;
  onClearSearch: () => void;
  onBackToPicks: () => void;
}

const emptyStateSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1,
  py: 6,
  px: 2,
  textAlign: 'center',
};

const emptyStateActionsSx: SxProps<Theme> = {
  mt: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap',
  justifyContent: 'center',
};

const fullBudgetIconSx: SxProps<Theme> = { fontSize: 40, color: 'primary.main' };

const mutedIconSx: SxProps<Theme> = { fontSize: 40, color: 'action.disabled' };

const bodySx: SxProps<Theme> = { maxWidth: FULL_BUDGET_BODY_MAX_WIDTH };

export const MarketplaceEmptyState = ({
  error,
  hasSpentCredits,
  filterName,
  hasFilters,
  query,
  hasPicksToReturnTo,
  onRetry,
  onOpenCart,
  onShowAllItems,
  onClearFilters,
  onClearFiltersKeepBudget,
  onClearSearch,
  onBackToPicks,
}: IProps) => {
  if (error) {
    return (
      <Box sx={emptyStateSx}>
        <Typography variant="body1" color="text.secondary">
          {error.message || DEFAULT_ERROR_MESSAGE}
        </Typography>
        <Button variant="text" color="primary" onClick={onRetry} data-action="retry_catalog_button">
          Retry
        </Button>
      </Box>
    );
  }

  const hasSearch = !!query;
  const isNarrowed = hasFilters || hasSearch;

  if (hasSpentCredits) {
    return (
      <Box sx={emptyStateSx}>
        {!isNarrowed && <CheckCircleOutlineIcon sx={fullBudgetIconSx} />}
        <Typography variant="subtitle1" fontWeight="medium">
          {isNarrowed ? getNothingElseFitsTitle(filterName, hasSearch) : FULL_BUDGET_TITLE}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={bodySx}>
          {isNarrowed ? NOTHING_ELSE_FITS_BODY : FULL_BUDGET_BODY}
        </Typography>
        <Box sx={emptyStateActionsSx}>
          {isNarrowed && (
            <Button
              variant="outlined"
              color="primary"
              onClick={onClearFiltersKeepBudget}
              data-action="clear_filters_button"
            >
              {CLEAR_FILTERS_LABEL}
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            aria-haspopup="dialog"
            onClick={onOpenCart}
            data-action="view_picks_button"
          >
            View picks
          </Button>
          <Button variant="text" color="primary" onClick={onShowAllItems} data-action="show_all_items_button">
            {SHOW_ALL_ITEMS_LABEL}
          </Button>
        </Box>
      </Box>
    );
  }

  if (isNarrowed) {
    let body = NO_RESULTS_BODY;

    if (hasFilters) {
      body = hasSearch ? NO_FILTER_MATCH_WITH_SEARCH_BODY : NO_FILTER_MATCH_BODY;
    }

    return (
      <Box sx={emptyStateSx}>
        {hasFilters ? <FilterAltOffOutlinedIcon sx={mutedIconSx} /> : <SearchOffIcon sx={mutedIconSx} />}
        <Typography variant="subtitle1" fontWeight="medium">
          {hasFilters ? NO_FILTER_MATCH_TITLE : getNoResultsTitle(query)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={bodySx}>
          {body}
        </Typography>
        <Box sx={emptyStateActionsSx}>
          <Button
            variant="outlined"
            color="primary"
            onClick={hasFilters ? onClearFilters : onClearSearch}
            data-action={hasFilters ? 'clear_filters_button' : 'clear_search_button'}
          >
            {hasFilters ? CLEAR_FILTERS_LABEL : CLEAR_SEARCH_LABEL}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={emptyStateSx}>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        {NOTHING_FITS_TEXT}
      </Typography>
      {hasPicksToReturnTo && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={onBackToPicks}
          data-action="back_to_picks_button"
        >
          Back to your options
        </Button>
      )}
    </Box>
  );
};
